// @greenlight-spec: 20-agent-plugins.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0040-multiple-plugin-marketplace-repositories.md

import * as React from 'react';
import { TriangleAlert } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import type { AgentKind, PluginMarketplaceStatus } from '@greenlight/shared';

interface AgentVisibilityNoteProps {
  agent: AgentKind;
  agentLabel: string;
  repository: PluginMarketplaceStatus;
  repositories: readonly PluginMarketplaceStatus[];
  privateInstall: boolean;
}

/**
 * A private repository breaks the main path only where every machine clones
 * it: Copilot, the Skill link for other agents, and Claude on a private
 * installation. Codex and Cursor organization installs serve a vendor copy.
 */
function mismatchCopy(
  agent: AgentKind,
  agentLabel: string,
  repositories: readonly PluginMarketplaceStatus[],
  repository: PluginMarketplaceStatus,
  privateInstall: boolean,
): string | null {
  if (repository.visibility === 'public') return null;
  if (agent === 'codex' || agent === 'cursor' || (agent === 'claude' && !privateInstall)) {
    return null;
  }
  const alternative = repositories.find((candidate) => candidate.visibility === 'public');
  const instead = alternative
    ? `Select ${alternative.repo_full_name} instead`
    : 'An organization admin can create a public one under Settings → Source control';
  if (agent === 'other') {
    return `This repository is private, so the Skill link opens only for people with GitHub access to it. ${instead}.`;
  }
  return `This repository is private, so ${agentLabel} installs only on machines with Git access to it. ${instead}.`;
}

/** Warns when the selected repository's visibility cannot serve the agent's main install path (ADR-0040). */
export function AgentVisibilityNote({
  agent,
  agentLabel,
  repository,
  repositories,
  privateInstall,
}: AgentVisibilityNoteProps): React.ReactElement | null {
  const mismatch = mismatchCopy(agent, agentLabel, repositories, repository, privateInstall);
  if (mismatch === null) return null;

  return (
    <Alert variant="warning" data-testid="agent-visibility">
      <TriangleAlert aria-hidden="true" />
      <AlertDescription>
        <p data-testid="agent-visibility-mismatch">{mismatch}</p>
      </AlertDescription>
    </Alert>
  );
}
